import React, { useEffect } from 'react';

import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink, NotificationBadge } from '../../../../components';

import css from './ParentBottomNav.module.css';

// The parent's mobile bottom navigation (per the client's design): Home, Bookings, Explore,
// Messages, Profile. Rendered by the Topbar on the main parent destinations only -- flow pages
// (class detail with its sticky Book bar, checkout, a conversation) keep their own bottom UI.

const IconHome = () => (
  <svg className={css.icon} viewBox="0 0 24 24" role="none">
    <path
      d="M4 10.5 12 4l8 6.5V19a1.5 1.5 0 0 1-1.5 1.5H15V14h-6v6.5H5.5A1.5 1.5 0 0 1 4 19v-8.5z"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconCalendar = () => (
  <svg className={css.icon} viewBox="0 0 24 24" role="none">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" fill="none" strokeWidth="1.8" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" fill="none" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M7.5 13h2M11 13h2M14.5 13h2M7.5 16.5h2M11 16.5h2" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconSearch = () => (
  <svg className={css.icon} viewBox="0 0 24 24" role="none">
    <circle cx="11" cy="11" r="6.5" fill="none" strokeWidth="1.8" />
    <path d="m16 16 4.5 4.5" fill="none" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconMail = () => (
  <svg className={css.icon} viewBox="0 0 24 24" role="none">
    <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" strokeWidth="1.8" />
    <path d="m4 7 8 6 8-6" fill="none" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const IconPerson = () => (
  <svg className={css.icon} viewBox="0 0 24 24" role="none">
    <circle cx="12" cy="8" r="3.75" fill="none" strokeWidth="1.8" />
    <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" fill="none" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// Tab definitions: where each tab links, and which route names light it up.
const TABS = [
  {
    key: 'home',
    labelId: 'ParentBottomNav.home',
    icon: <IconHome />,
    linkName: 'LandingPage',
    activeOn: ['LandingPage'],
  },
  {
    key: 'bookings',
    labelId: 'ParentBottomNav.bookings',
    icon: <IconCalendar />,
    linkName: 'BookingsBasePage',
    activeOn: ['BookingsBasePage', 'BookingsPage'],
  },
  {
    key: 'explore',
    labelId: 'ParentBottomNav.explore',
    icon: <IconSearch />,
    linkName: 'SearchPage',
    activeOn: ['SearchPage', 'SearchPageWithListingType'],
  },
  {
    key: 'messages',
    labelId: 'ParentBottomNav.messages',
    icon: <IconMail />,
    linkName: 'InboxPage',
    linkParams: { tab: 'orders' },
    activeOn: ['InboxPage'],
    showBadge: true,
  },
  {
    key: 'profile',
    labelId: 'ParentBottomNav.profile',
    icon: <IconPerson />,
    linkName: 'ParentInfoPage',
    activeOn: ['ParentInfoPage', 'AccountSecurityPage', 'ProfileSettingsPage', 'PaymentMethodsPage'],
  },
];

// The main destinations where the bar shows. Flow pages (class detail, checkout, a conversation)
// are deliberately absent -- they have their own bottom UI (sticky Book bar, pay button, composer).
const SHOW_ON_PAGES = new Set(TABS.flatMap(tab => tab.activeOn));

// The topbar suffixes some resolved names with context ("InboxPage:orders", "CMSPage:about");
// match on the base route name.
const baseName = currentPage => (currentPage || '').split(':')[0];

export const shouldShowParentBottomNav = currentPage => SHOW_ON_PAGES.has(baseName(currentPage));

/**
 * @param {Object} props
 * @param {string} props.currentPage resolved route name (drives the active tab)
 * @param {number} [props.notificationCount] unread notifications for the Messages badge
 */
const ParentBottomNav = ({ currentPage, notificationCount = 0 }) => {
  // Reserve room for the fixed bar so page bottoms (pagination, last cards) stay reachable. The
  // matching rule lives in marketplaceDefaults.css, scoped to the same mobile breakpoint.
  useEffect(() => {
    document.body.classList.add('withParentBottomNav');
    return () => document.body.classList.remove('withParentBottomNav');
  }, []);

  return (
    <nav className={css.root} aria-label="Main">
      {TABS.map(tab => {
        const isActive = tab.activeOn.includes(baseName(currentPage));
        return (
          <NamedLink
            key={tab.key}
            name={tab.linkName}
            {...(tab.linkParams ? { params: tab.linkParams } : {})}
            className={isActive ? css.tabActive : css.tab}
          >
            <span className={css.iconWrapper}>
              {tab.icon}
              {tab.showBadge && notificationCount > 0 ? (
                <NotificationBadge className={css.badge} count={notificationCount} />
              ) : null}
            </span>
            <span className={css.label}>
              <FormattedMessage id={tab.labelId} />
            </span>
          </NamedLink>
        );
      })}
    </nav>
  );
};

export default ParentBottomNav;
